<template>
  <div class="py-4">
    <div v-if="isLoading" class="text-center py-4">
      <v-progress-circular indeterminate color="primary" size="24" />
    </div>

    <p v-else-if="!details" class="grey--text text-center mb-0">
      {{ $t('financial.breakdown.unavailable') }}
    </p>

    <div v-else>
      <div
        v-for="realty in realties"
        :key="realty.realtyId"
        class="mb-6"
      >
        <h4 class="mb-2">
          {{ realty.code || '---' }}
          <span class="body-2 grey--text">{{ realty.address }}</span>
        </h4>
        <v-simple-table dense>
          <thead>
            <tr>
              <th />
              <th class="text-right">{{ $t('financial.breakdown.realEstate') }}</th>
              <th class="text-right">{{ $t('financial.breakdown.host') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in realty.rows" :key="row.key">
              <td :class="{ 'font-weight-bold': row.key === 'total' }">
                {{ $t(`financial.breakdown.${row.key}`) }}
              </td>
              <td class="text-right" :class="{ 'font-weight-bold': row.key === 'total' }">
                {{ formatValue(row.realEstate) }}
              </td>
              <td class="text-right" :class="{ 'font-weight-bold': row.key === 'total' }">
                {{ formatValue(row.host) }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div v-if="additionals.length" class="mb-6">
        <h4 class="mb-2">{{ $t('financial.breakdown.additionals') }}</h4>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>{{ $t('financial.breakdown.description') }}</th>
              <th class="text-right">{{ $t('financial.breakdown.realEstate') }}</th>
              <th class="text-right">{{ $t('financial.breakdown.host') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="additional in additionals" :key="additional.id">
              <td>{{ additional.description || '---' }}</td>
              <td class="text-right">{{ formatValue(additional.value) }}</td>
              <td class="text-right">{{ formatValue(additional.host_value) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div class="d-flex justify-end">
        <div class="mr-8">
          <span class="grey--text">{{ $t('financial.breakdown.realEstateTotal') }}:</span>
          <strong>{{ formatValue(details.total) }}</strong>
        </div>
        <div>
          <span class="grey--text">{{ $t('financial.breakdown.hostTotal') }}:</span>
          <strong>{{ formatValue(details.host_total) }}</strong>
        </div>
      </div>

      <p v-if="semiFurnishedInsideFurniture" class="caption grey--text mt-4 mb-0">
        {{ $t('financial.breakdown.legacySemiFurnished') }}
      </p>
    </div>
  </div>
</template>

<script>
import { catalogMonetary } from '@/util/catalog'
import { getPaymentFinancialDetails } from '@/services/financial/financialService'

// Additionals in display order. Base and total always show; the others only when charged.
const ADDITIONAL_KEYS = [
  'area_additional',
  'furniture_additional',
  'semi_furniture_additional',
  'house_additional',
  'trousseau_additional',
  'urgency_additional',
]

const ROW_KEYS = {
  area_additional: 'area',
  furniture_additional: 'furniture',
  semi_furniture_additional: 'semiFurniture',
  house_additional: 'house',
  trousseau_additional: 'trousseau',
  urgency_additional: 'urgency',
}

const toNumber = (value) => Number(value ?? 0)

export default {
  name: 'PaymentFinancialDetails',

  props: {
    paymentId: {
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      details: null,
    }
  },

  computed: {
    realties() {
      const hostEntries = this.details.host_financial_detail_entries || []
      return (this.details.financial_detail_entries || []).map((entry) => {
        const hostEntry = hostEntries.find((host) => host.realty_id === entry.realty_id) || {}
        return {
          realtyId: entry.realty_id,
          code: entry.realty_code,
          address: entry.realty_address,
          rows: this.buildRows(entry, hostEntry),
        }
      })
    },

    additionals() {
      return this.details.additional_list || []
    },

    /**
     * Payments calculated before the semi-furnished additional got its own field carry it inside the
     * furniture one; those entries come without the field at all.
     */
    semiFurnishedInsideFurniture() {
      return (this.details.financial_detail_entries || []).some(
        (entry) => entry.semi_furniture_additional == null
      )
    },
  },

  mounted() {
    this.fetchDetails()
  },

  // The table may reuse this instance for another row, e.g. after a new payment shifts the list
  watch: {
    paymentId() {
      this.fetchDetails()
    },
  },

  methods: {
    async fetchDetails() {
      this.isLoading = true
      await getPaymentFinancialDetails(this.paymentId)
        .then(({ data }) => (this.details = data))
        .catch(() => (this.details = null))
        .finally(() => (this.isLoading = false))
    },

    buildRows(entry, hostEntry) {
      const charged = ADDITIONAL_KEYS.filter(
        (key) => toNumber(entry[key]) !== 0 || toNumber(hostEntry[key]) !== 0
      )
      // The host entry has no base price: it is what is left of its total after the additionals
      const hostBase = ADDITIONAL_KEYS.reduce(
        (base, key) => base - toNumber(hostEntry[key]),
        toNumber(hostEntry.total)
      )
      const rows = [{ key: 'basePrice', realEstate: entry.base_price, host: hostBase }]
      if (toNumber(entry.discount) !== 0) {
        rows.push({ key: 'discount', realEstate: entry.discount, host: null })
      }
      charged.forEach((key) =>
        rows.push({ key: ROW_KEYS[key], realEstate: entry[key], host: hostEntry[key] })
      )
      rows.push({ key: 'total', realEstate: entry.total, host: hostEntry.total })
      return rows
    },

    formatValue(value) {
      return value == null ? '---' : catalogMonetary(Number(value))
    },
  },
}
</script>
